import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit2, Loader2, GripVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CategoriesManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    label: "",
    order: 0
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchCategories();
  }, [user, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des catégories");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.label) {
      toast.error("Veuillez remplir tous les champs requis");
      return;
    }

    try {
      if (editingCategory) {
        await axios.patch(`${API}/categories/${editingCategory.id}`, {
          label: formData.label,
          order: formData.order
        });
        toast.success("Catégorie modifiée avec succès");
      } else {
        await axios.post(`${API}/categories`, formData);
        toast.success("Catégorie ajoutée avec succès");
      }
      
      fetchCategories();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette catégorie ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/categories/${id}`);
      setCategories(categories.filter(cat => cat.id !== id));
      toast.success("Catégorie supprimée avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const toggleCategoryActive = async (category) => {
    try {
      await axios.patch(`${API}/categories/${category.id}`, {
        is_active: !category.is_active
      });
      setCategories(categories.map(c => 
        c.id === category.id ? { ...c, is_active: !c.is_active } : c
      ));
      toast.success(category.is_active ? "Catégorie désactivée" : "Catégorie activée");
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      label: category.label,
      order: category.order
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: "",
      label: "",
      order: categories.length
    });
  };

  const initDefaultCategories = async () => {
    try {
      const response = await axios.post(`${API}/init-default-categories`);
      toast.success(response.data.message);
      fetchCategories();
    } catch (error) {
      toast.error("Erreur lors de l'initialisation");
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate("/sites")}
              variant="outline"
              className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-5xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Gestion des Catégories
              </h1>
              <p className="text-lg text-gray-400 mt-2">
                {categories.length} catégorie(s) configurée(s)
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            {categories.length === 0 && (
              <Button
                onClick={initDefaultCategories}
                variant="outline"
                className="bg-transparent border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              >
                Initialiser les catégories par défaut
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-category-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une catégorie
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-gray-800 text-white">
                <DialogHeader>
                  <DialogTitle data-testid="dialog-title">
                    {editingCategory ? "Modifier la catégorie" : "Ajouter une nouvelle catégorie"}
                  </DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Les catégories permettent d'organiser vos sites de recherche
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nom technique (ID) *</Label>
                    <Input
                      data-testid="category-name-input"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Ex: social, business, osint"
                      disabled={!!editingCategory}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ne peut pas être modifié après création
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="label">Label affiché *</Label>
                    <Input
                      data-testid="category-label-input"
                      id="label"
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Ex: Réseaux sociaux, Entreprises"
                    />
                  </div>
                  <div>
                    <Label htmlFor="order">Ordre d'affichage</Label>
                    <Input
                      data-testid="category-order-input"
                      id="order"
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Plus petit = affiché en premier
                    </p>
                  </div>
                  <Button
                    data-testid="submit-category-btn"
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {editingCategory ? "Modifier" : "Ajouter"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Categories List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : categories.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center">
            <p className="text-xl text-gray-400 mb-4">Aucune catégorie configurée</p>
            <Button onClick={initDefaultCategories} className="bg-blue-600 hover:bg-blue-700">
              Initialiser les catégories par défaut
            </Button>
          </div>
        ) : (
          <div className="glass rounded-2xl p-6">
            <div className="space-y-3">
              {categories.map((category) => (
                <div
                  key={category.id}
                  data-testid={`category-card-${category.id}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-gray-900/30 border border-gray-800 hover:border-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <GripVertical className="w-5 h-5 text-gray-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{category.label}</h3>
                        {!category.is_active && (
                          <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30">
                            Désactivée
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">
                        ID: <span className="font-mono">{category.name}</span> • Ordre: {category.order}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${category.id}`} className="text-sm">
                        {category.is_active ? "Active" : "Inactive"}
                      </Label>
                      <Switch
                        data-testid={`toggle-active-${category.id}`}
                        id={`active-${category.id}`}
                        checked={category.is_active}
                        onCheckedChange={() => toggleCategoryActive(category)}
                      />
                    </div>
                    <Button
                      data-testid={`edit-category-${category.id}`}
                      onClick={() => openEditDialog(category)}
                      size="sm"
                      variant="outline"
                      className="bg-transparent border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`delete-category-${category.id}`}
                      onClick={() => deleteCategory(category.id)}
                      size="sm"
                      variant="destructive"
                      className="bg-red-600/20 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesManagementPage;
